import moment from "moment";
import { useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/input/Select";
import { Badge } from "src/components/ui/Badge";
import { LeaveRequest } from "src/redux/types/_attendance.type";
import { useLeaveMutation } from "../leaveManagement/hooks/useLeaveMutation";
import { DocumentPreview } from "../leaveManagement/components/DocumentPreview";
import { AlertTriangle } from "lucide-react";

interface LeaveRequestCardProps {
  leave: LeaveRequest;
}

const DOCUMENT_REQUIRED_TYPES = ["MTL", "PTL"];

const requiresDocument = (leave: LeaveRequest) => {
  if (DOCUMENT_REQUIRED_TYPES.includes(leave?.type)) return true;
  if (leave?.type === "SL" && Number(leave?.days) > 1) return true;
  return false;
};

const leaveTypeMap: Record<string, string> = {
  CL: "Casual Leave",
  SL: "Sick Leave",
  PLL: "Planned Leave",
  MTL: "Maternity Leave",
  PTL: "Paternity Leave",
  LWP: "Leave Without Pay",
};

const borderColour = {
  APPROVED: "border-green-200",
  REJECTED: "border-red-200",
  PENDING: "border-amber-200",
};

const rejectionReasons = [
  "Documents not clear",
  "Document not submitted",
  "Insufficient Leave balance",
  "Business / Workload Requirements",
  "Short Notice",
  "Policy Violation",
  "Unjustified Leave Reason",
];

const LeaveRequestCard = ({ leave }: LeaveRequestCardProps) => {
  const [showReasonSelect, setShowReasonSelect] = useState(false);
  const [selectedReason, setSelectedReason] = useState("");

  const docRequired = requiresDocument(leave);

  const { mutate: statusChange, isPending } = useLeaveMutation();

  const handleRequest = (status: "APPROVED" | "REJECTED") => {
    statusChange(
      { id: leave.id, status, reason: selectedReason },
      {
        onSuccess: () => {
          setShowReasonSelect(false);
          setSelectedReason("");
        },
      },
    );
  };

  return (
    <div
      className={`w-[500px] h-fit bg-card rounded-2xl shadow-lg p-4 border border-gray-200 space-y-3 mx-auto ${borderColour[leave.status]}`}
    >
      <div className="flex justify-between">
        <div className="space-y-1">
          <h2 className="text-sm font-semibold text-gray-800 capitalize">
            {leave.employee_name}
          </h2>
          <p className="text-xs text-gray-600">{leave.employee_email}</p>

          <p className="text-xs text-gray-500 font-medium">{leave.post}</p>
        </div>
        <Badge
          className={`px-2 py-1 h-fit rounded-full uppercase text-xs font-medium
                  ${
                    leave.status === "APPROVED"
                      ? "bg-green-50 text-green-600"
                      : leave.status === "REJECTED"
                        ? "bg-red-50 text-red-600"
                        : "bg-amber-50 text-amber-500"
                  }
                `}
        >
          {leave.status}
        </Badge>
      </div>

      <div className="text-sm text-gray-600 space-y-1">
        <div className="flex justify-between">
          <span>
            From:{" "}
            <span className="font-medium text-gray-800">
              {moment(leave.from_date).format("MMM Do YY")}
            </span>
          </span>
          <span>
            To:{" "}
            <span className="font-medium text-gray-800">
              {moment(leave.to_date).format("MMM Do YY")}
            </span>
          </span>
        </div>

        <div className="my-3 flex justify-between">
          <p>
            <span className="font-medium text-gray-800 mr-2">Leave Type:</span>
            <span className="px-2 py-0.5 text-xs bg-blue-400 text-white rounded-full">
              {leaveTypeMap[leave?.type] || leave?.type}
            </span>
          </p>
          <p className="text-sm">
            Requested On: {moment(leave.created_at).format("MMM Do YY")}
          </p>
        </div>
        <p>
          <span className="font-medium text-gray-800 mr-2">Reason:</span>
          {leave.reason}
        </p>
      </div>

      <div className="">
        {leave.rejection_reason && (
          <p className="text-sm text-gray-800">
            <span className="font-medium">Rejection Reason</span>:{" "}
            {leave.rejection_reason}
          </p>
        )}
      </div>

      <div>
        {docRequired && (
          <div className="mt-4">
            {leave.documentation ? (
              <DocumentPreview url={leave.documentation} />
            ) : (
              <div className="flex items-center gap-2 rounded-xl border border-dashed border-amber-300 bg-amber-50 p-3">
                <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
                <p className="text-xs text-amber-700">
                  No supporting document uploaded for this leave type.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex justify-evenly gap-3 pt-3 border-t border-gray-300">
        {!showReasonSelect && (
          <button
            className="px-3 py-1 rounded-lg text-xs border border-green-400 text-green-700 bg-green-50 hover:bg-green-100 transition"
            onClick={() => handleRequest("APPROVED")}
            disabled={isPending}
          >
            Approve
          </button>
        )}

        {!showReasonSelect ? (
          <button
            className="px-3 py-1 rounded-lg text-xs border border-red-400 text-red-700 bg-red-50 hover:bg-red-100 transition"
            onClick={() => setShowReasonSelect(true)}
          >
            Reject
          </button>
        ) : (
          <div className="flex gap-3 justify-evenly items-center w-full">
            <Select onValueChange={(value) => setSelectedReason(value)}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a reason" />
              </SelectTrigger>
              <SelectContent>
                {rejectionReasons.map((reason, idx) => (
                  <SelectItem key={idx} value={reason}>
                    {reason}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <button
              className="px-3 py-1 rounded-lg text-xs border border-red-400 text-red-700 bg-red-50 hover:bg-red-100 transition"
              onClick={() => handleRequest("REJECTED")}
              disabled={isPending || !selectedReason}
            >
              {isPending ? (
                <span className="loading loading-dots loading-sm"></span>
              ) : (
                "Confirm"
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeaveRequestCard;
